/**
 * **Wohin „Neue Aufgabe“ fällt**, wenn man sie aus der Listen-Werkzeug-Pille zieht (#301).
 *
 * Gerechnet auf der Lage, die man SIEHT — mit der offenen Lücke darin. Steht der Zeiger in der
 * offenen Lücke, bleibt sie das Ziel; sonst flatterte sie an ihrer Kante hin und her (Prototyp
 * `tools/new-task-drag-prototype`, 10.10.2026).
 *
 * Bewusst ohne DOM-Bezug, wie die übrigen `utils/`: Die Seite misst die Rechtecke, hier wird nur
 * gewählt.
 */

/** Abstand zwischen zwei Zeilen (`.task-list { gap: 2px }`). */
export const LIST_ROW_GAP = 2

/** Weiter weg als so viele Pixel von jeder Lücke: kein Ziel. */
const MAX_SLOT_DISTANCE = 60
/** So weit links und rechts neben der Liste zählt der Zeiger noch zur Liste. */
const SIDE_SLACK = 40

/**
 * Ein Ziel: eine Lücke zwischen Zeilen (`afterTaskId`, `null` = oben im Abschnitt bzw. im leeren
 * Abschnitt) — oder der Kopf eines zugeklappten Abschnitts: für die Aufgabe „ans Ende“ (`end`), für einen
 * neuen Abschnitt „danach“ (`after`, #314).
 */
export type NewTaskDropTarget =
  | { kind: 'slot'; segmentIndex: number; afterTaskId: string | null }
  | { kind: 'end'; segmentIndex: number; sectionId: string }
  | { kind: 'after'; segmentIndex: number; sectionId: string }

/** Wo im Abschnitt eine neue Aufgabe landen darf. `top`: nur ganz oben (Kurz notiert ohne „Letzte 7 Tage“). */
export type NewTaskDropAccepts = 'all' | 'top' | 'none'

export interface NewTaskDropSegment {
  /** Index in den Segmenten, die die Liste zeichnet. */
  index: number
  accepts: NewTaskDropAccepts
  sectionId: string | null
  /** Rechteck des Kopfs, wenn der Abschnitt zugeklappt ist — sonst `null`. */
  collapsedHeader: { top: number; bottom: number } | null
  /** Die sichtbaren Zeilen von oben nach unten; zugeklappt keine. */
  rows: ReadonlyArray<{ id: string; top: number; bottom: number }>
  /** Wo die Lücke ohne Zeilen stünde (leerer oder zugeklappter Abschnitt). */
  emptyAt: number | null
}

export interface PickNewTaskDropTargetInput {
  segments: ReadonlyArray<NewTaskDropSegment>
  pointer: { x: number; y: number }
  /** Die Liste auf dem Bildschirm. */
  bounds: { left: number; right: number; top: number; bottom: number }
  /** Die gerade offene Lücke samt Rechteck. */
  openGap: { target: NewTaskDropTarget; top: number; bottom: number } | null
  /**
   * Der sichtbare Bereich der Liste (ohne den Streifen unter der Pille). Was darüber oder darunter
   * liegt, ist kein Ziel — wer über den Rand hinaus loslässt, will abbrechen (Diff-Audit 10.10.2026:
   * getroffen wurde eine oben weggerollte Zeile). Fehlt er, zählt alles.
   */
  viewport?: { top: number; bottom: number }
  /** Das bisherige Ziel — ein Kopf „ans Ende“ hält es auch dort, wohin er gleich rutscht. */
  current?: NewTaskDropTarget | null
  /** Höhe der Lücke. */
  gapHeight?: number
  /**
   * Was der Kopf eines zugeklappten Abschnitts meint: `end` — die Aufgabe kommt ans Ende, der Abschnitt
   * geht auf; `after` — ein neuer Abschnitt kommt hinter ihn, er bleibt zu (#314).
   */
  collapsedHeader?: 'end' | 'after'
}

export function sameDropTarget(a: NewTaskDropTarget | null, b: NewTaskDropTarget | null): boolean {
  if (a === b) return true
  if (!a || !b || a.kind !== b.kind || a.segmentIndex !== b.segmentIndex) return false
  return a.kind === 'slot' ? a.afterTaskId === (b as typeof a).afterTaskId : a.sectionId === (b as typeof a).sectionId
}

/**
 * Die Lücken eines Abschnitts als Linien: je Lücke die Höhe, auf der sie liegt. Zwischen zwei
 * Zeilen ist das die Mitte des Abstands — steht dort die offene Lücke, ist es ihre Mitte.
 */
export function slotLines(segment: NewTaskDropSegment): Array<{ afterTaskId: string | null; y: number }> {
  if (segment.accepts === 'none') return []
  /*
    Zugeklappt ist der Kopf das Ziel („ans Ende“), keine Lücke darunter. Sonst sprang das Ziel knapp
    unter dem Kopf zwischen beiden hin und her, und unter ihm blitzte eine halb offene Lücke als
    Haarlinie auf (gemeldet 10.10.2026). Nimmt der Abschnitt nur oben an (Kurz notiert ohne „Letzte
    7 Tage“), bleibt die Lücke — sein Kopf ist dann kein Ziel.
  */
  if (segment.collapsedHeader && segment.accepts === 'all') return []
  const { rows } = segment
  if (rows.length === 0) return segment.emptyAt == null ? [] : [{ afterTaskId: null, y: segment.emptyAt }]
  const lines = [{ afterTaskId: null as string | null, y: rows[0].top - LIST_ROW_GAP / 2 }]
  if (segment.accepts === 'top') return lines
  rows.forEach((row, i) => {
    const next = rows[i + 1]
    lines.push({ afterTaskId: row.id, y: next ? (row.bottom + next.top) / 2 : row.bottom + LIST_ROW_GAP / 2 })
  })
  return lines
}

/**
 * Das Ziel unter dem Zeiger — oder `null`, wenn er zu weit weg ist.
 *
 * Reihenfolge: zuerst die Köpfe zugeklappter Abschnitte (sie liegen sonst zwischen zwei Lücken und
 * würden überstimmt), dann die offene Lücke als Haltezone, dann die nächste Lücke.
 *
 * @example
 * pickNewTaskDropTarget({ segments, pointer: { x, y }, bounds, openGap: null })
 * // → { kind: 'slot', segmentIndex: 0, afterTaskId: 'a' }
 */
export function pickNewTaskDropTarget({
  segments, pointer, bounds, viewport, openGap, current = null, gapHeight = 0, collapsedHeader = 'end',
}: PickNewTaskDropTargetInput): NewTaskDropTarget | null {
  if (pointer.x < bounds.left - SIDE_SLACK || pointer.x > bounds.right + SIDE_SLACK) return null
  const visible = (y: number) => !viewport || (y >= viewport.top && y <= viewport.bottom)
  if (!visible(pointer.y)) return null

  for (const segment of segments) {
    const header = segment.collapsedHeader
    if (!header || segment.accepts !== 'all' || !segment.sectionId) continue
    /*
      Haltezone nach unten: Wird der Kopf zum Ziel, schließt sich die Lücke darüber, und der Kopf
      rutscht unter dem stehenden Zeiger um ihre Höhe nach oben. Ohne sie fiele der Zeiger unter den
      Kopf, die Lücke darüber öffnete sich wieder, schöbe den Kopf zurück — im Kreis, als flackernde
      Haarlinie über dem Kopf (gemeldet 10.10.2026).
    */
    /*
      „Danach“ öffnet die Lücke UNTER dem Kopf — der Zeiger, der hineinfährt, hält das Ziel, wie oben
      beschrieben.
    */
    const holding = current?.kind === collapsedHeader && current.segmentIndex === segment.index
    const below = holding ? gapHeight : 0
    if (pointer.y >= header.top - LIST_ROW_GAP && pointer.y <= header.bottom + LIST_ROW_GAP + below) {
      return { kind: collapsedHeader, segmentIndex: segment.index, sectionId: segment.sectionId }
    }
  }

  // „Danach“ (#314) hält seine Lücke ebenso: Sie steht unter dem Kopf mit Abstand, nicht direkt an ihm.
  const holdsGap = openGap?.target.kind === 'slot' || openGap?.target.kind === 'after'
  if (openGap && holdsGap && pointer.y >= openGap.top - LIST_ROW_GAP && pointer.y <= openGap.bottom + LIST_ROW_GAP) {
    return openGap.target
  }

  let best: { distance: number; target: NewTaskDropTarget } | null = null
  for (const segment of segments) {
    for (const line of slotLines(segment)) {
      if (!visible(line.y)) continue
      const distance = Math.abs(pointer.y - line.y)
      if (!best || distance < best.distance) {
        best = { distance, target: { kind: 'slot', segmentIndex: segment.index, afterTaskId: line.afterTaskId } }
      }
    }
  }
  return best && best.distance < MAX_SLOT_DISTANCE ? best.target : null
}

/** Was das Loslassen anlegt — dieselben Wege wie Tastatur und Klick (`useTaskCreation`). */
export type NewTaskCreation =
  | { kind: 'after'; taskId: string }
  | { kind: 'position'; position: 'top' | 'bottom'; segmentIndex: number; expandSectionId: string | null }

/**
 * Lücke → Anlegen. Nach einer Zeile: hinter ihr (`startCreatingAfter`). Oben bzw. im leeren
 * Abschnitt: oben im Abschnitt — beide Ersteller stehen dort an derselben Stelle wie die Lücke.
 * Kopf eines zugeklappten Abschnitts: aufklappen, dann ans Ende.
 */
export function creationForDropTarget(target: NewTaskDropTarget): NewTaskCreation {
  // `after` gibt es nur im Abschnitts-Zug (#314); käme er hier an, hieße er dasselbe wie der Kopf.
  if (target.kind !== 'slot') {
    return { kind: 'position', position: 'bottom', segmentIndex: target.segmentIndex, expandSectionId: target.sectionId }
  }
  if (target.afterTaskId) return { kind: 'after', taskId: target.afterTaskId }
  return { kind: 'position', position: 'top', segmentIndex: target.segmentIndex, expandSectionId: null }
}

/** Die Wege, die das Loslassen nimmt — dieselben wie per Tastatur (`useTaskCreation`). */
export interface NewTaskDropActions {
  startCreating: (position: 'top' | 'bottom', segmentIndex: number, options: { landed: true }) => boolean
  startCreatingAfter: (taskId: string, options: { landed: true }) => boolean
  /** Einen zugeklappten Abschnitt aufklappen. */
  expand: (sectionId: string) => void
}

/**
 * Loslassen über einem Ziel: anlegen, und für „ans Ende“ den Abschnitt aufklappen — aber nur, wenn
 * der Ersteller wirklich aufgeht. Steht schon einer offen (⌘N mitten im Zug) oder wurde eben einer
 * abgebrochen, bliebe der Abschnitt sonst offen, während die Fläche wegfällt (Diff-Audit 10.10.2026).
 * `true`, wenn angelegt wird.
 */
export function applyNewTaskDrop(target: NewTaskDropTarget, actions: NewTaskDropActions): boolean {
  const creation = creationForDropTarget(target)
  if (creation.kind === 'after') return actions.startCreatingAfter(creation.taskId, { landed: true })
  const opened = actions.startCreating(creation.position, creation.segmentIndex, { landed: true })
  // Im selben Bild wie der Ersteller — beide Zustände landen in einem Rendern.
  if (opened && creation.expandSectionId) actions.expand(creation.expandSectionId)
  return opened
}
